import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatCard from "@/components/StatCard";
import StatPatientsDialog from "@/components/StatPatientsDialog";
import { Bed, Wind, Activity, Sun, HeartPulse, LogIn, LogOut, AlertTriangle, Baby } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function useStats() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    let ok = true;
    const load = async () => {
      try {
        const [s, a] = await Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/alerts")]);
        if (!ok) return;
        setStats(s.data);
        setAlerts(a.data);
      } catch (e) { console.warn("Dashboard load failed", e); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { ok = false; clearInterval(t); };
  }, []);
  return { stats, alerts };
}

export default function Dashboard() {
  const { stats, alerts } = useStats();
  const s = stats || {};
  const [list, setList] = useState(null);
  const open = (kind, label) => () => setList({ kind, label });

  return (
    <div className="space-y-8" data-testid="reception-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Reception · Live overview</div>
          <h1 className="font-display text-3xl font-semibold mt-1">NICU Command Centre</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time bed occupancy, respiratory support and clinical alerts.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admit"><Button className="rounded-full" data-testid="btn-admit-quick"><Baby size={16} className="mr-2" /> Admit patient</Button></Link>
          <Link to="/beds"><Button variant="outline" className="rounded-full" data-testid="btn-view-bedmap">View bed map</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        <StatCard testid="stat-total-beds" label="Total NICU beds" value={s.total_beds ?? "—"} tone="primary" icon={Bed} />
        <StatCard testid="stat-occupied" label="Occupied" value={s.occupied_beds ?? "—"} tone="warn" icon={Bed} sub={`${s.vacant_beds ?? 0} vacant`} />
        <StatCard testid="stat-vacant" label="Vacant" value={s.vacant_beds ?? "—"} tone="good" icon={Bed} />
        <StatCard testid="stat-critical" label="Critical babies" value={s.critical_babies ?? "—"} tone="crit" icon={AlertTriangle} onClick={open("critical", "Critical babies")} />
        <StatCard testid="stat-vent" label="On ventilator" value={s.on_ventilator ?? "—"} tone="crit" icon={Wind} />
        <StatCard testid="stat-cpap" label="On CPAP" value={s.on_cpap ?? "—"} tone="warn" icon={Wind} />
        <StatCard testid="stat-hfnc" label="On HFNC" value={s.on_hfnc ?? "—"} tone="warn" icon={Wind} onClick={open("hfnc", "Babies on HFNC")} />
        <StatCard testid="stat-photo" label="Phototherapy" value={s.on_phototherapy ?? "—"} tone="photo" icon={Sun} onClick={open("phototherapy", "Babies on phototherapy")} />
        <StatCard testid="stat-adm-today" label="Admissions today" value={s.admissions_today ?? "—"} tone="primary" icon={LogIn} onClick={open("admissions_today", "Admissions today")} />
        <StatCard testid="stat-dis-today" label="Discharges today" value={s.discharges_today ?? "—"} tone="neutral" icon={LogOut} onClick={open("discharges_today", "Discharges today")} />
      </div>
      {list && <StatPatientsDialog list={list} onClose={() => setList(null)} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-clinical p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Ward pulse</h3>
            <Badge variant="secondary" className="rounded-full"><HeartPulse size={12} className="mr-1" /> Live</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Overview of respiratory support distribution.</p>
          <div className="mt-6 grid grid-cols-3 gap-4">
            {[
              { k: "Ventilator", v: s.on_ventilator, tone: "bg-critical" },
              { k: "CPAP", v: s.on_cpap, tone: "bg-warn" },
              { k: "HFNC", v: s.on_hfnc, tone: "bg-warn" },
            ].map((r) => (
              <div key={r.k} className={`rounded-xl px-4 py-4 ${r.tone}`}>
                <div className="text-[11px] uppercase tracking-widest opacity-75">{r.k}</div>
                <div className="font-display text-3xl font-semibold tabular mt-1">{r.v ?? 0}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-clinical p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Alerts</h3>
            <Badge variant="secondary" className="rounded-full">{alerts.length}</Badge>
          </div>
          <div className="mt-4 space-y-2 max-h-[320px] overflow-auto pr-1">
            {alerts.length === 0 && <div className="text-sm text-muted-foreground">No active alerts. All patients stable.</div>}
            {alerts.map((a, i) => (
              <div key={`${a.kind}:${a.patient_id || a.order_id || i}:${a.text}`} className={`rounded-lg px-3 py-2 text-sm flex items-start gap-2 ${a.kind === "critical" ? "bg-critical" : "bg-warn"}`}>
                <AlertTriangle size={14} className="mt-0.5" />
                <div>
                  <div className="font-medium">{a.text}</div>
                  {a.patient_name && <div className="text-xs opacity-80">{a.patient_name}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
